import React from 'react';
import Select from 'react-select';
import { AlertCircle } from 'lucide-react';

// Style personnalisé pour react-select
const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '42px',
    borderColor: state.isFocused ? '#202654' : state.isHovered ? '#cbd5e1' : '#e2e8f0',
    boxShadow: state.isFocused ? '0 0 0 1px #202654' : 'none',
    '&:hover': {
      borderColor: state.isFocused ? '#202654' : '#cbd5e1'
    },
    borderRadius: '0.375rem',
    backgroundColor: '#ffffff',
    fontSize: '0.875rem'
  }),
  menu: (base) => ({
    ...base,
    zIndex: 50,
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    borderRadius: '0.375rem'
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected 
      ? 'rgba(32, 38, 84, 0.9)' 
      : state.isFocused 
        ? 'rgba(32, 38, 84, 0.1)' 
        : base.backgroundColor,
    color: state.isSelected ? 'white' : '#4b5563',
    fontSize: '0.875rem',
    padding: '8px 12px',
    cursor: 'pointer',
    '&:active': {
      backgroundColor: 'rgba(32, 38, 84, 0.2)'
    }
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: 'rgba(32, 38, 84, 0.1)',
    borderRadius: '0.25rem'
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: '#202654',
    fontSize: '0.75rem',
    padding: '2px 6px'
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: '#202654',
    '&:hover': {
      backgroundColor: '#202654',
      color: 'white'
    }
  }),
  placeholder: (base) => ({
    ...base,
    color: '#94a3b8',
    fontSize: '0.875rem'
  }),
  singleValue: (base) => ({
    ...base,
    color: '#1f2937',
    fontSize: '0.875rem'
  }),
  indicatorSeparator: (base) => ({
    ...base,
    backgroundColor: '#e2e8f0'
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: '#94a3b8',
    '&:hover': {
      color: '#475569'
    }
  })
};

const FormInput = ({
  id,
  label,
  type = 'text',
  placeholder,
  value,
  onChange,
  error,
  required = false,
  disabled = false,
  className = '',
  options = [],
  isMulti = false,
  icon: Icon,
  max,
  min,
  step,
  rows = 3,
  helpText,
  name
}) => {
  const inputId = id || name || `form-input-${Math.random().toString(36).substring(2, 9)}`;
  
  const renderInput = () => {
    switch (type) {
      case 'select':
        return (
          <select
            id={inputId}
            name={name}
            value={value}
            onChange={onChange}
            disabled={disabled}
            required={required}
            className={`
              block w-full rounded-md border-gray-300 shadow-sm 
              transition-colors duration-200 ease-in-out
              focus:border-[#202654] focus:ring focus:ring-[#202654] focus:ring-opacity-20
              disabled:bg-gray-100 disabled:cursor-not-allowed
              text-sm
              ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500 focus:ring-opacity-20' : ''}
              ${Icon ? 'pl-10' : ''}
              ${className}
            `}
          >
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      
      case 'react-select':
        return (
          <Select
            inputId={inputId}
            name={name}
            value={
              isMulti
                ? options.filter(option => (Array.isArray(value) ? value : []).includes(option.value))
                : options.find(option => option.value === value)
            }
            onChange={(selectedOption) => {
              if (isMulti) {
                onChange(selectedOption ? selectedOption.map(option => option.value) : []);
              } else {
                onChange(selectedOption ? selectedOption.value : '');
              }
            }}
            options={options}
            placeholder={placeholder}
            isDisabled={disabled}
            isMulti={isMulti}
            styles={customSelectStyles}
            className={className}
            classNamePrefix="react-select"
            noOptionsMessage={() => "Aucune option disponible"}
          />
        );
      
      case 'textarea':
        return (
          <textarea
            id={inputId}
            name={name}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            rows={rows}
            className={`
              block w-full rounded-md border-gray-300 shadow-sm 
              transition-colors duration-200 ease-in-out
              focus:border-[#202654] focus:ring focus:ring-[#202654] focus:ring-opacity-20
              disabled:bg-gray-100 disabled:cursor-not-allowed
              text-sm
              ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500 focus:ring-opacity-20' : ''}
              ${className}
            `}
          />
        );
      
      case 'number':
        return (
          <input
            id={inputId}
            name={name}
            type="number"
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            max={max}
            min={min}
            step={step}
            className={`
              block w-full rounded-md border-gray-300 shadow-sm 
              transition-colors duration-200 ease-in-out
              focus:border-[#202654] focus:ring focus:ring-[#202654] focus:ring-opacity-20
              disabled:bg-gray-100 disabled:cursor-not-allowed
              text-sm
              ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500 focus:ring-opacity-20' : ''}
              ${Icon ? 'pl-10' : ''}
              ${className}
            `}
          />
        );
      
      default:
        return (
          <input
            id={inputId}
            name={name}
            type={type}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            className={`
              block w-full rounded-md border-gray-300 shadow-sm 
              transition-colors duration-200 ease-in-out
              focus:border-[#202654] focus:ring focus:ring-[#202654] focus:ring-opacity-20
              disabled:bg-gray-100 disabled:cursor-not-allowed
              text-sm
              ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500 focus:ring-opacity-20' : ''}
              ${Icon ? 'pl-10' : ''}
              ${className}
            `}
          />
        );
    }
  };

  return (
    <div className={`${className.includes('mb-0') ? 'mb-0' : 'mb-4'}`}>
      {label && (
        <label 
          htmlFor={inputId} 
          className={`block text-sm font-medium ${error ? 'text-red-500' : 'text-gray-700'} mb-1`}
        >
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      
      <div className="relative">
        {Icon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Icon className={`h-5 w-5 ${error ? 'text-red-500' : 'text-gray-400'}`} />
          </div>
        )}
        
        {renderInput()}
        
        {error && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <AlertCircle className="h-5 w-5 text-red-500" />
          </div>
        )}
      </div>
      
      {helpText && (
        <p className="mt-1 text-xs text-gray-500">{helpText}</p>
      )}
      
      {error && (
        <p className="mt-1 text-xs text-red-500">{error}</p>
      )}
    </div>
  );
};

export default FormInput; 